import React, { useEffect, useState } from "react";
import RulesListDataTable from "../DataGrids/RulesListDataGrid";
import useFetchPaginatedNullKeysValidations from "../../hooks/useFetchPaginatedNullKeysValidations";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { IconIncidentDetails } from "../../common/utils/icons";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-pro";
import DashboardUserDetails from "../Molecules/Rule/DashboardUserDetails";
import IncidentUserandStatusDialog from "../Molecules/Rule/IncidentUserandStatusDialog";
import StatusCell from "../../common/utils/statusIcon";
import {
  ClassificationColumn,
  CommentColumn,
  CurrentStatusColumn,
  ReasonColumn,
  UserColumn,
} from "../../common/utils/commonActionsFields";
import Loader from "../Molecules/Loader/Loader";
import { useAuth } from "../../contexts/AuthContext";

import {
  calculateDaysInQueue,
  getFormattedDateTime,
  deduplicateByFieldProperty,
} from "../../services/utils";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";

interface INullKeyErrors {
  validateResult: any;
  userDialogData: any;
  setUserDialogData: React.Dispatch<React.SetStateAction<any>>;
  allUsersList: any;
}

const NullKeyErrors = ({
  validateResult,
  userDialogData,
  setUserDialogData,
  allUsersList,
}: INullKeyErrors) => {
  const { hasPermission } = useAuth();
  const canEditValidationIssues = hasPermission("validation_issues", "edit");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [columnsData, setColumnsData] = useState<any[]>([]);
  const [rowsData, setRowsData] = useState<any[]>([]);
  const [dashboardUserDetailsData, setDashboardUserDetailsData] = useState({
    status: false,
    assigned_user: "",
    issueId: null,
    isBackDropVisible: false,
  });
  const [selectedIds, setSelectedIds] = useState<any>([]);
  const [resultColumns, setResultColumns] = useState<any>([]);

  //for pagination default values and states
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  const [nullKeysValidation] = useFetchPaginatedNullKeysValidations({
    currentValidationResultId: validateResult?.id,
    setIsLoading,
    page,
    pSize,
    isDetailedExecutionDataAvailable:
      validateResult?.is_detailed_execution_data_available,
    isIssuesDataAvailable: validateResult?.is_issues_data_available ?? true,
  });

  useEffect(() => {
    setUserDialogData((prev: any) => ({
      ...prev,
      selectedIds: selectedIds || [],
    }));
  }, [selectedIds]);

  // Functions for handling "WithoutIssue" data structure
  const getColumnsWithoutIssue = (firstItem: any) => {
    // For null key errors, resource_keys doesn't exist, so we use record_data fields
    const recordData = firstItem?.record_snapshot?.record_data;

    // Error message column
    const errorMessageColumn = {
      field: "error_message",
      headerName: "ERROR MESSAGE",
      flex: 1,
      minWidth: 280,
      renderCell: (params: any) => (
        <div className="word-break-all">{params.value}</div>
      ),
    };

    // File name column
    const fileNameColumn = {
      field: "file_name",
      headerName: "FILE_NAME(S)",
      flex: 1,
      minWidth: 240,
      renderCell: (params: any) => (
        <div
          className="data-grid-cell word-break-all"
          dangerouslySetInnerHTML={{ __html: params.value }}
        />
      ),
    };

    let columns: any[] = [errorMessageColumn, fileNameColumn];

    // Add record snapshot columns (since no resource_keys for null key errors)
    if (recordData) {
      const snapshot_columns = Object.keys(recordData)
        .filter((key) => key !== "file_name" && key !== "ErrorMessage")
        .map((key) => ({
          field: key,
          headerName: key,
          flex: 1,
          minWidth: 140,
        }));
      columns = [...columns, ...snapshot_columns];
    }

    // Ensure no duplicate columns exist based on the "field" property
    return deduplicateByFieldProperty(columns, "field");
  };

  const getRowsWithoutIssue = (items: any[]) => {
    const rows = items.map((item: any, index: any) => {
      const fileNamesArray = item?.file_name?.replace(/'/g, "").split(";");
      const formattedFileNames =
        fileNamesArray && fileNamesArray.length > 1
          ? `<ul class="ul-inside-grid">${fileNamesArray
              .map((name: any) => `<li>${name}</li>`)
              .join("")}</ul>`
          : `<div class="word-break-all">${
              (fileNamesArray && fileNamesArray[0]) || ""
            }</div>`;

      // Calculate days in queue
      const diffInDays = calculateDaysInQueue(item.first_occurrence);

      return {
        id: index,
        rowId: item?.id,
        ...item,
        file_name: formattedFileNames,
        first_occurrence: item?.first_occurrence,
        days_in_queue: diffInDays,
        ...item?.record_snapshot?.record_data,
      };
    });

    return rows;
  };

  useEffect(() => {
    if (nullKeysValidation?.items?.length > 0) {
      const isIssuesDataAvailable =
        validateResult?.is_issues_data_available ?? true;

      let columns, rows;

      if (isIssuesDataAvailable) {
        // "WithIssue" data structure - has null_key_error_record_details
        if (nullKeysValidation?.items[0]?.null_key_error_record_details) {
          const firstItem =
            nullKeysValidation?.items[0]?.null_key_error_record_details;
          const resourceKeys = firstItem?.resource_keys;
          const recordData = firstItem?.record_snapshot?.resource_data;
          let columnsArray: any = [];
          const resource_keys_columns: any = resourceKeys
            ? Object.keys(resourceKeys).map((key) => ({
                field: key,
                headerName: key,
                flex: 1,
                minWidth: 120,
              }))
            : [];
          const fileNameColumn: any = [
            {
              field: "file_name",
              headerName: "File_Name(S)",
              flex: 1,
              minWidth: 240,
              renderCell: (params: any) => (
                <div
                  className="data-grid-cell word-break-all"
                  dangerouslySetInnerHTML={{ __html: params.value }}
                />
              ),
            },
          ];
          const errorMessageColumn: any = [
            {
              field: "error_message",
              headerName: "Error_Message",
              flex: 1,
              minWidth: 240,
            },
          ];
          const daysInQueueColumn: any = [
            {
              field: "days_in_queue",
              headerName: "Age",
              flex: 1,
              minWidth: 110,
              renderCell: (params: any) => {
                return (
                  <Box>
                    {params?.row?.first_occurrence === null ? (
                      params.value ?? 0
                    ) : (
                      <Tooltip
                        title={
                          <Typography
                            sx={{ fontSize: "0.875rem" }}
                          >{`First Occurrence : ${getFormattedDateTime(
                            params?.row?.first_occurrence
                          )}`}</Typography>
                        }
                        placement="top"
                        className="tooltip-ellipsis"
                      >
                        {params.value ?? 0}
                      </Tooltip>
                    )}
                  </Box>
                );
              },
            },
          ];
          const issueColumns: any = [
            CurrentStatusColumn({}),
            UserColumn({}),
            CommentColumn({}),
            ClassificationColumn({}),
            ReasonColumn({}),
            {
              field: "action",
              headerName: "Actions",
              align: "center",
              headerAlign: "center",
              minWidth: "130",
              pinned: "right",
              renderCell: (params: any, index: any) => {
                return (
                  <>
                    {canEditValidationIssues && (
                      <Box sx={{ display: "inline-flex", columnGap: "12px" }}>
                        <Tooltip
                          title={`Incident Details : ${params?.row?.id}`}
                          placement="top"
                          arrow
                        >
                          <Button
                            className="btn-blue btn-orange btn-sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setDashboardUserDetailsData((prev: any) => {
                                return {
                                  ...prev,
                                  status: true,
                                  incidentId: params.row.rowId,
                                  assigned_user: params.row.assigned_user,
                                  panelType: "incidentDetail",
                                  isBackDropVisible: true,
                                  first_occurrence: params.row.first_occurrence,
                                  issue_classification_id:
                                    params.row.issue_classification_id,
                                  issue_reason_id: params.row.issue_reason_id,
                                  comment: [
                                    {
                                      comment: params.row.comment,
                                      create_date: params.row.create_date,
                                      assigned_user:
                                        params.row?.assigned_user || "",
                                    },
                                  ],
                                  current_status: params.row.current_status,
                                  issueId: params.row.rowId,
                                };
                              });
                            }}
                          >
                            <IconIncidentDetails />
                          </Button>
                        </Tooltip>
                      </Box>
                    )}
                  </>
                );
              },
            },
          ];
          columnsArray = [
            ...resource_keys_columns,
            ...fileNameColumn,
            ...errorMessageColumn,
            ...daysInQueueColumn,
            ...issueColumns,
          ];
          if (recordData) {
            const snapshot_columns = recordData
              ? Object.keys(recordData)
                  .filter(
                    (key) =>
                      !resource_keys_columns.some(
                        (column: { field: string }) => column.field === key
                      ) &&
                      !fileNameColumn.some(
                        (column: { field: string }) => column.field === key
                      ) &&
                      key !== "Error_Message"
                  )
                  .map((key) => ({
                    field: key,
                    headerName: key,
                    flex: 1,
                    minWidth: 120,
                  }))
              : [];

            columnsArray = [...columnsArray, ...snapshot_columns];
          }

          const rowsArray = nullKeysValidation.items.map(
            (item: any, index: any) => {
              const fileNamesArray =
                item?.null_key_error_record_details?.file_name
                  ?.replace(/'/g, "")
                  .split(";");
              const formattedFileNames =
                fileNamesArray && fileNamesArray.length > 1
                  ? `<ul class="ul-inside-grid">${fileNamesArray
                      .map((name: any) => `<li>${name}</li>`)
                      .join("")}</ul>`
                  : `<div class="word-break-all">${
                      (fileNamesArray && fileNamesArray[0]) || ""
                    }</div>`;

              // Calculate days in queue
              const diffInDays = calculateDaysInQueue(item.first_occurrence);

              return {
                id: item?.id,
                rowId: item?.id,
                ...item,
                error_message:
                  item?.null_key_error_record_details?.error_message,
                ...item?.null_key_error_record_details?.resource_keys,
                file_name: formattedFileNames,
                first_occurrence: item?.first_occurrence,
                days_in_queue: diffInDays,
                ...item?.null_key_error_record_details?.record_snapshot
                  ?.resource_data,
              };
            }
          );

          columns = columnsArray;
          rows = rowsArray;
        }
      } else {
        // "WithoutIssue" data structure - data is directly on the item
        const firstItem = nullKeysValidation.items[0];
        columns = getColumnsWithoutIssue(firstItem);
        rows = getRowsWithoutIssue(nullKeysValidation.items);
      }

      if (columns && rows) {
        setColumnsData(columns);
        setRowsData(rows);
        // Notify parent that data is available
        setUserDialogData((prev: any) => ({
          ...prev,
          hasNullKeyErrorsData: true,
        }));
      }
    } else {
      setColumnsData([]);
      setRowsData([]);
      // Notify parent that no data is available
      setUserDialogData((prev: any) => ({
        ...prev,
        hasNullKeyErrorsData: false,
      }));
    }
  }, [nullKeysValidation, validateResult?.is_issues_data_available]);
  const [isAllColumnPinned, setIsAllColumnPinned] = useState(false);
  const [pinnedColumns, setPinnedColumns] = useState({
    left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
    right: ["current_status", "username", "comment", "action"],
  });
  /**
   * Determine if checkbox selection should be enabled
   * Checkbox is only shown when:
   *   1. There are data rows available, AND
   *   2. is_issues_data_available is true
   */
  const isCheckboxRequired =
    Array.isArray(rowsData) &&
    rowsData.length > 0 &&
    validateResult?.is_issues_data_available === true;

  const handleMoveColumns = () => {
    setPinnedColumns((prevState) => {
      const columnsToAdd = ["current_status", "username", "comment"];
      const isColumnsAvailable = columnsToAdd.every((item) =>
        prevState.right.includes(item)
      );

      setIsAllColumnPinned(isColumnsAvailable);
      if (isColumnsAvailable) {
        return {
          left: prevState.left,
          right: prevState.right.filter((item) => !columnsToAdd.includes(item)),
        };
      } else {
        return {
          left: [...prevState.left],
          right: [...columnsToAdd, ...prevState.right],
        };
      }
    });
  };
  return (
    <>
      <Box className="position-relative">
        {(dashboardUserDetailsData?.isBackDropVisible || isLoading) && (
          <Box className="adhock-query-accordion-wrapper">
            <Loader
              isAbsolute={true}
              isLoading={
                dashboardUserDetailsData?.isBackDropVisible || isLoading
              }
              radius={{ top: 0, right: 0, bottom: 5, left: 5 }}
            />
          </Box>
        )}
        <Box>
          <Box className="position-relative">
            {rowsData.length > 0 &&
              validateResult?.is_issues_data_available && (
                <IconButton
                  onClick={handleMoveColumns}
                  className={`icon-pin-datagrid navbar-icon burger  ${
                    isAllColumnPinned ? "active" : ""
                  }`}
                >
                  <div className="bar bar1"></div>
                  <div className="bar bar2"></div>
                  <div className="bar bar3"></div>
                </IconButton>
              )}

            {rowsData && rowsData?.length !== 0 ? (
              <RulesListDataTable
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                setResultColumns={setResultColumns}
                dataRows={rowsData || []}
                dataColumns={columnsData || []}
                loading={isLoading}
                className="dataTable no-radius pt-0 bdr-top-0 datatable-column-sep checkbox-result-column remove-pinnedColumnHeaders-bg pinnedColumnHeaders-bg-height hide-progress-icon"
                disableColumnFilter={true}
                tableHeight={180}
                disableColumnReorder={true}
                paginationMode="server"
                rowCount={nullKeysValidation?.total || 0}
                paginationModel={{
                  page: page - 1,
                  pageSize: pSize,
                }}
                onPaginationModelChange={(params: any) => {
                  if (params.pageSize !== pSize || params.page !== page - 1) {
                    setPage(params.page + 1);
                    setPSize(params.pageSize);
                  }
                }}
                singlePageMaxHeightDiff={300}
                pinnedColumns={rowsData?.length > 0 ? pinnedColumns : {}}
                disableColumnPinning={false}
                checkboxSelection={isCheckboxRequired}
                isExtraSpaceNeedToBeRemoved={true}
              />
            ) : (
              <Box
                className="adhock-query-accordion-wrapper"
                sx={{
                  paddingTop: "24px",
                  paddingBottom: "24px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                No data available
              </Box>
            )}
          </Box>
          <DashboardUserDetails
            dashboardUserDetailsData={dashboardUserDetailsData}
            setDashboardUserDetailsData={setDashboardUserDetailsData}
            allUsersList={allUsersList}
            setIsLoading={setIsLoading}
            setDetailViewDataRows={setRowsData}
            issueFrom={"validation"}
          />
          <IncidentUserandStatusDialog
            userDialogData={userDialogData}
            setUserDialogData={setUserDialogData}
            allUsersList={allUsersList}
            setDetailViewDataRows={setRowsData}
            issueFrom={"validation"}
            setSelectedIds={setSelectedIds}
          />
        </Box>
      </Box>
    </>
  );
};

export default NullKeyErrors;
